<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0"
            >
                <h2
                    class="font-weight-bold text-base text-gray-800 leading-tight"
                >
                    {{
                        $t(
                            "calculator.Calculator for selecting a photovoltaic installation"
                        )
                    }}
                </h2>
            </v-card-title>

            <v-divider class="mt-6" />

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
                <div>
                    <v-text-field
                        v-model.number="annualKwhConsumption"
                        :label="
                            $t('calculator.Annual energy consumption kwh')
                        "
                        variant="outlined"
                        density="compact"
                        type="number"
                    />

                    <v-text-field
                        v-model.number="heatingBills"
                        :label="$t('calculator.Annual heating bills')"
                        variant="outlined"
                        density="compact"
                        type="number"
                    />

                    <v-text-field
                        v-model.number="batterySize"
                        :label="$t('calculator.The size of the energy storage')"
                        variant="outlined"
                        density="compact"
                        type="number"
                    />

                    <checkbox-input
                        v-model="eastWestInstallation"
                        class="pb-2 pr-5 w-full"
                        :label="$t('calculator.East-West Roof')"
                    />

                    <checkbox-input
                        v-model="hasEv"
                        class="pb-2 pr-5 w-full"
                        :label="$t('calculator.Electric car at home')"
                    />

                    <checkbox-input
                        v-model="isOptimised"
                        class="pb-2 pr-5 w-full"
                        :label="$t('calculator.Optimizing energy consumption')"
                    />

                    <checkbox-input
                        v-model="staysAtHome"
                        class="pb-2 pr-5 w-full"
                        :label="$t('calculator.Work from home retirement')"
                    />

                    <checkbox-input
                        v-model="hasHeatPump"
                        class="pb-2 pr-5 w-full"
                        :label="$t('calculator.Heat pump in the house')"
                    />

                    <v-text-field
                        v-model.number="kwhBuyPrice"
                        :label="$t('calculator.Purchase price in kwh')"
                        class="mt-4"
                        variant="outlined"
                        density="compact"
                        type="number"
                    />

                    <v-text-field
                        v-model.number="kwhSellPrice"
                        :label="$t('calculator.Selling price in kwh')"
                        variant="outlined"
                        density="compact"
                        type="number"
                    />
                </div>

                <div>
                    <h4>
                        {{ $t("calculator.Your self-consumption") }}:
                        {{ parseInt(autoConsumption * 100) }}%
                    </h4>

                    <h4>
                        {{ $t("calculator.Installation size required") }}:
                        {{ recommendedInstallationSize }} kwp
                    </h4>

                    <v-divider class="my-4" />

                    <p>
                        {{ $t("calculator.Current annual bills") }}:
                        <b> {{ $numberHelper.formatMoney(oldBills) }} </b>
                    </p>

                    <p>
                        {{ $t("calculator.New annual bills") }}:
                        <b v-if="newBills > 0">
                            {{ $numberHelper.formatMoney(newBills) }}
                        </b>

                        <b v-else class="text-green-500">
                            {{ $numberHelper.formatMoney(newBills) }} -
                            {{ $t("calculator.you earn") }}!
                        </b>
                    </p>

                    <p class="mt-3">
                        {{ $t("calculator.Bills for 25 years without PV") }}:
                        <b>
                            {{
                                $numberHelper.formatMoney(
                                    oldBillsArray[oldBillsArray.length - 1]
                                )
                            }}
                        </b>
                    </p>

                    <p>
                        {{ $t("calculator.Bills for 25 years with PV") }}:
                        <b v-if="newBillsArray[newBillsArray.length - 1] > 0">
                            {{
                                $numberHelper.formatMoney(
                                    newBillsArray[newBillsArray.length - 1]
                                )
                            }}
                        </b>

                        <b v-else class="text-green-500">
                            {{
                                $numberHelper.formatMoney(
                                    newBillsArray[newBillsArray.length - 1]
                                )
                            }}
                            - {{ $t("calculator.you earn") }}!
                        </b>
                    </p>

                    <p class="border-top">
                        {{ $t("calculator.Profit") }}:
                        <b class="text-green-400">
                            {{
                                $numberHelper.formatMoney(
                                    oldBillsArray[oldBillsArray.length - 1] -
                                        newBillsArray[newBillsArray.length - 1]
                                )
                            }}
                        </b>
                    </p>

                    <v-text-field
                        v-model.number="inflation"
                        :label="$t('calculator.Inflation')"
                        variant="outlined"
                        class="mt-3"
                        density="compact"
                        type="number"
                        prefix="%"
                    />
                </div>

                <div>
                    <pv-savings-chart
                        :oldBillsArray="oldBillsArray"
                        :newBillsArray="newBillsArray"
                    />
                </div>
            </div>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";
import BreezeInput from "@/Components/Input";
import DownArrowIcon from "@/Components/Icons/DownArrowIcon";
import TextInput from "@/Components/Shared/TextInput";
import CheckboxInput from "@/Components/Shared/CheckboxInput";
import PvSavingsChart from "@/Components/PvSavingsChart";

export default {
    props: {},
    components: {
        PvSavingsChart,
        CheckboxInput,
        TextInput,
        DownArrowIcon,
        AuthenticatedLayout,
        BreezeInput,
    },
    data() {
        return {
            annualKwhConsumption: 10000,
            kwhBuyPrice: 0.71,
            kwhSellPrice: 0.31,
            eastWestInstallation: false,
            refundFactor: 1.2,
            hasHeatPump: false,
            hasEv: false,
            isOptimised: false,
            staysAtHome: false,
            batterySize: 0,
            inflation: 0,
            heatingBills: 0,
        };
    },
    computed: {
        recommendedInstallationSize() {
            return (
                (this.annualKwhConsumption * this.autoConsumption +
                    (this.refundFactor *
                        this.annualKwhConsumption *
                        (1 - this.autoConsumption)) /
                        (this.kwhSellPrice / this.kwhBuyPrice)) /
                this.pvRadiation
            ).toFixed(2);
        },
        autoConsumption() {
            let ac = 0.25;
            if (this.batterySize) {
                ac += (this.batterySize / 5) * 0.15;
            }
            if (this.hasHeatPump) {
                ac += 0.1;
            }
            if (this.hasEv) {
                ac += 0.08;
            }
            if (this.isOptimised) {
                ac += 0.15;
            }
            if (this.staysAtHome) {
                ac += 0.1;
            }
            if (ac >= 0.9) {
                return 0.9;
            }
            return ac;
        },
        pvRadiation() {
            return this.eastWestInstallation ? 850 : 1080;
        },
        oldBills() {
            let electricityBills = this.annualKwhConsumption * this.kwhBuyPrice;
            if (this.heatingBills) {
                return electricityBills + this.heatingBills;
            }
            return electricityBills;
        },
        newBills() {
            let kwhBoughtFromGrid =
                this.annualKwhConsumption * (1 - this.autoConsumption);
            let kwhSoldToGrid =
                this.annualKwhConsumption * this.autoConsumption;
            return (
                kwhBoughtFromGrid * this.kwhBuyPrice -
                kwhSoldToGrid * this.kwhSellPrice
            );
        },
        oldBillsArray() {
            return this.getBillsOverYears(this.oldBills);
        },
        newBillsArray() {
            return this.getBillsOverYears(this.newBills);
        },
    },
    mounted() {},
    methods: {
        getBillsOverYears(annualBill) {
            let inflation = 1 + this.inflation / 100,
                yearData = [];
            for (let i = 0; i < 25; i++) {
                let newBills = 0,
                    previousYear = yearData[yearData.length - 1];
                if (i === 0) {
                    newBills = annualBill;
                } else {
                    newBills = parseInt(
                        (previousYear + annualBill) * inflation
                    );
                }
                yearData.push(newBills);
            }
            return yearData;
        },
    },
};
</script>

<style lang="scss">
.page h3 {
    font-size: 1.2rem;
    font-weight: bold;
}

.text-sm {
    font-size: 0.8rem;
}

.page .table {
    margin-bottom: 20px;
    font-size: 0.8rem;
    width: 100%;

    td,
    th {
        padding: 1px 6px;
    }

    th {
        text-align: left;
    }

    .btn {
        font-size: 0.8rem;
        padding: 0.1rem 0.4rem;
    }
}
</style>
